import type { PetCopyWire } from '@kenocyber/shared';
import { findChassisFinish, findEnergyColor } from '@kenocyber/shared';

/**
 * Which COPY the player is acting on.
 *
 * This exists because a pet is an instance, not a kind. Food, paint and - when
 * trading arrives - market value all belong to one row, so "feed the pet"
 * is not a well-formed instruction when four of them are sitting in the vault.
 * The panel used to pick the active one, or the strongest, and quietly feed
 * that; a player with two orbs had no way to say which.
 *
 * Every option therefore states everything that distinguishes it: form, active
 * state, both training counters, colour, finish, and a short id so two
 * otherwise identical copies are still tellable apart.
 */

interface PetCopyPickerProps {
  readonly copies: readonly PetCopyWire[];
  readonly selectedId: string | null;
  readonly onSelect: (instanceId: string) => void;
  /** Disables every option while a request is in flight. */
  readonly disabled?: boolean;
  /**
   * The still image of a given evolution, or null if the manifest has none.
   *
   * A FUNCTION rather than a URL, because each copy shows its OWN form: a
   * vault holding an MK I and an MK III must not label both with the same
   * picture. The caller resolves it from the asset manifest the panel already
   * has, so nothing here downloads anything the screen was not going to load
   * anyway, and no third renderer is created to draw a 42-pixel square.
   */
  readonly artUrl?: (evolution: number) => string | null;
}

/** Basis points as a signed percentage: 150 -> `+1,5%`. */
export function bpsLabel(value: number): string {
  return `+${(value / 100).toFixed(1).replace('.', ',')}%`;
}

/**
 * The last six characters of the uuid.
 *
 * Enough to tell two copies apart at a glance, and deliberately not the whole
 * thing: a full uuid on screen is noise, and printing identifiers a player
 * never needs is a habit worth not forming.
 */
export function shortId(instanceId: string): string {
  return instanceId.slice(-6).toUpperCase();
}

/** A one-line summary of what makes this copy this copy. */
export function describeCopy(copy: PetCopyWire): string {
  const parts: string[] = [`EV.${copy.evolution}`];
  if (copy.active) {
    parts.push('ATIVO');
  }
  const trained = copy.training.damageFoodCount > 0 || copy.training.attackSpeedFoodCount > 0;
  if (trained) {
    if (copy.training.damageBonusBps > 0) {
      parts.push(`DANO ${bpsLabel(copy.training.damageBonusBps)}`);
    }
    if (copy.training.attackSpeedBonusBps > 0) {
      parts.push(`ASPD ${bpsLabel(copy.training.attackSpeedBonusBps)}`);
    }
  } else {
    parts.push('SEM TREINO');
  }
  const finish = findChassisFinish(copy.finishId);
  // The finish is named only when it is not the one every copy starts in -
  // repeating "Branco Pérola" on four rows tells the player nothing.
  if (finish && copy.finishId !== 'finish_pearl_white') {
    parts.push(finish.name.toUpperCase());
  }
  return parts.join(' · ');
}

/** The form and its state: the line a player scans first. */
export function copyHeadline(copy: PetCopyWire): string {
  return copy.active ? `EV.${copy.evolution} · ATIVO` : `EV.${copy.evolution}`;
}

/** Training and paint: what makes one copy of a form different from another. */
export function copyDetail(copy: PetCopyWire): string {
  const parts: string[] = [];
  if (copy.training.damageBonusBps > 0) {
    parts.push(`DANO ${bpsLabel(copy.training.damageBonusBps)}`);
  }
  if (copy.training.attackSpeedBonusBps > 0) {
    parts.push(`ASPD ${bpsLabel(copy.training.attackSpeedBonusBps)}`);
  }
  if (parts.length === 0) {
    parts.push('SEM TREINO');
  }
  const finish = findChassisFinish(copy.finishId);
  if (finish && copy.finishId !== 'finish_pearl_white') {
    parts.push(finish.name.toUpperCase());
  }
  return parts.join(' · ');
}

export function PetCopyPicker({
  copies,
  selectedId,
  onSelect,
  disabled,
  artUrl,
}: PetCopyPickerProps) {
  if (copies.length === 0) {
    return null;
  }
  return (
    <section className="pet-copies" data-testid="pet-copy-picker">
      <h4>Cópia selecionada</h4>
      <div className="pet-copy-list">
        {copies.map((copy) => {
          const energy = findEnergyColor(copy.energyId);
          const selected = copy.instanceId === selectedId;
          const art = artUrl?.(copy.evolution) ?? null;
          return (
            <button
              key={copy.instanceId}
              type="button"
              aria-pressed={selected}
              className="pet-copy-option"
              data-selected={selected ? 'true' : 'false'}
              data-active={copy.active ? 'true' : 'false'}
              data-testid={`pet-copy-option-${copy.instanceId}`}
              disabled={disabled}
              onClick={() => onSelect(copy.instanceId)}
            >
              {/* The picture of THIS copy's form. A row that says "EV.3" and
                  shows an MK I would be worse than no picture at all, so the
                  evolution is on the element for a spec to read. */}
              <span
                className="pet-copy-art"
                data-testid={`pet-copy-art-${copy.instanceId}`}
                data-evolution={copy.evolution}
                aria-hidden="true"
              >
                {art ? (
                  <img
                    src={art}
                    alt=""
                    draggable={false}
                    data-testid={`pet-copy-art-img-${copy.instanceId}`}
                  />
                ) : (
                  <span className="pet-copy-art-fallback">EV.{copy.evolution}</span>
                )}
                {copy.active ? (
                  <span
                    className="pet-copy-badge"
                    title="Pet ativo"
                    data-testid={`pet-copy-active-${copy.instanceId}`}
                  >
                    E
                  </span>
                ) : null}
              </span>
              <span className="pet-copy-text" data-testid={`pet-copy-text-${copy.instanceId}`}>
                <span className="pet-copy-headline">
                  <span
                    className="pet-copy-energy"
                    style={{
                      background: energy
                        ? `#${energy.color.toString(16).padStart(6, '0')}`
                        : '#888',
                    }}
                    aria-hidden="true"
                  />
                  {copyHeadline(copy)}
                </span>
                <span className="pet-copy-detail">{copyDetail(copy)}</span>
                <span className="pet-copy-id">{shortId(copy.instanceId)}</span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
